import { useEffect, useRef, useState } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api/client";
import ChatMarkdown, { linkifyPlain, messageText } from "./ChatMarkdown";
import { useListingsStore, type ListingsFilters } from "../stores/listingsStore";

interface ChatMessage {
  ts: string;
  role: "user" | "assistant";
  content?: unknown;
  text?: string;
  error?: boolean;
}

interface ChatContext {
  page: "listing_detail" | "listings_search" | "other";
  listing_id?: string;
  filters?: Record<string, unknown>;
}

interface UiAction {
  action: "set_filters" | "highlight_listings" | "clear_highlight";
  input: Record<string, unknown>;
}

interface ChatReply {
  reply: string;
  stop_reason?: string;
  ui_actions?: UiAction[];
}

export default function FloatingChat() {
  const [open, setOpen] = useState<boolean>(() => {
    return localStorage.getItem("ma-chat-open") === "true";
  });
  const location = useLocation();

  // Hide entirely on the dedicated /chat page (avoid double UIs).
  if (location.pathname === "/chat") return null;

  function toggle() {
    const next = !open;
    setOpen(next);
    localStorage.setItem("ma-chat-open", String(next));
  }

  return (
    <>
      <button
        onClick={toggle}
        title={open ? "Close chat" : "Ask about your listings"}
        style={{
          position: "fixed",
          bottom: 16,
          right: 16,
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "var(--accent)",
          color: "white",
          border: 0,
          fontSize: 22,
          cursor: "pointer",
          boxShadow: "0 4px 14px rgba(0, 0, 0, 0.4)",
          zIndex: 100,
          display: open ? "none" : "grid",
          placeItems: "center",
        }}
        aria-label="Open chat"
      >
        💬
      </button>

      {open && <ChatPanel onClose={toggle} />}
    </>
  );
}

function ChatPanel({ onClose }: { onClose: () => void }) {
  const ctx = usePageContext();
  const qc = useQueryClient();
  const setFilters = useListingsStore((s) => s.setFilters);
  const setHighlight = useListingsStore((s) => s.setHighlight);
  const clearHighlight = useListingsStore((s) => s.clearHighlight);

  // Each listing has its own saved conversation; other pages share one.
  const thread = ctx.page === "listing_detail" && ctx.listing_id ? ctx.listing_id : "general";
  const threadQuery =
    thread === "general" ? "" : `?listing_id=${encodeURIComponent(thread)}`;
  const historyKey = ["chat-history", thread];

  const q = useQuery<{ messages: ChatMessage[] }>({
    queryKey: historyKey,
    queryFn: () => apiGet(`/api/chat/history${threadQuery}`),
  });

  const send = useMutation({
    mutationFn: (message: string) =>
      apiPost<ChatReply>("/api/chat/messages", { message, context: ctx }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: historyKey });
      for (const a of data.ui_actions ?? []) {
        applyUiAction(a, { setFilters, setHighlight, clearHighlight });
      }
    },
  });

  const clear = useMutation({
    mutationFn: () => apiDelete<{ ok: boolean }>(`/api/chat/history${threadQuery}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: historyKey }),
  });

  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [q.data?.messages, send.isPending]);

  function submit(e?: React.FormEvent) {
    if (e) e.preventDefault();
    const text = input.trim();
    if (!text || send.isPending) return;
    setInput("");
    send.mutate(text);
  }

  const messages = q.data?.messages ?? [];
  const placeholder =
    ctx.page === "listing_detail"
      ? "Ask about this listing — 'is this priced well vs comps?'"
      : "Ask about your listings — 'best schools under $1.5M?'";

  return (
    <div
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 400,
        maxWidth: "calc(100vw - 32px)",
        height: 560,
        maxHeight: "calc(100vh - 32px)",
        background: "var(--panel)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        boxShadow: "0 10px 32px rgba(0, 0, 0, 0.5)",
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "var(--panel)",
        }}
      >
        <strong style={{ flex: 1, fontSize: 14 }}>Ask Claude</strong>
        <ContextBadge ctx={ctx} />
        <button
          onClick={() => clear.mutate()}
          disabled={messages.length === 0 || clear.isPending}
          title={thread === "general" ? "Clear conversation" : "Clear this listing's conversation"}
          style={iconBtnStyle}
        >
          ⟲
        </button>
        <button onClick={onClose} title="Close" style={iconBtnStyle}>
          ✕
        </button>
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 12,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          background: "var(--bg)",
        }}
      >
        {q.isLoading && <p className="muted" style={{ margin: 0, fontSize: 12 }}>Loading…</p>}
        {messages.length === 0 && !q.isLoading && (
          <Suggestions ctx={ctx} onPick={(t) => send.mutate(t)} />
        )}
        {messages.map((m, i) => (
          <Bubble key={`${m.ts}-${i}`} message={m} />
        ))}
        {send.isPending && <p className="muted" style={{ margin: 0, fontSize: 12 }}>Thinking…</p>}
        {send.error && (
          <p className="tag-error" style={{ margin: 0, fontSize: 12 }}>
            Error: {String(send.error)}
          </p>
        )}
      </div>

      <form
        onSubmit={submit}
        style={{
          display: "flex",
          gap: 6,
          padding: 10,
          borderTop: "1px solid var(--border)",
          background: "var(--panel)",
        }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={placeholder}
          rows={2}
          style={{
            flex: 1,
            resize: "none",
            fontFamily: "inherit",
            fontSize: 12,
            padding: 6,
          }}
          disabled={send.isPending}
        />
        <button type="submit" disabled={!input.trim() || send.isPending}>
          ➤
        </button>
      </form>
    </div>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--border)",
  color: "var(--muted)",
  width: 26,
  height: 26,
  borderRadius: 6,
  cursor: "pointer",
  padding: 0,
  fontSize: 12,
};

function ContextBadge({ ctx }: { ctx: ChatContext }) {
  if (ctx.page === "listing_detail" && ctx.listing_id) {
    return (
      <span
        title={`Conversation context: ${ctx.listing_id}`}
        style={{
          fontSize: 10,
          padding: "2px 7px",
          borderRadius: 8,
          background: "var(--bg)",
          border: "1px solid var(--border)",
          color: "var(--muted)",
          fontFamily: "ui-monospace, monospace",
          maxWidth: 130,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {ctx.listing_id}
      </span>
    );
  }
  return null;
}

function usePageContext(): ChatContext {
  const location = useLocation();
  // FloatingChat renders outside <Routes>, where useParams() is always empty;
  // matching the path directly is what makes the listing reach the backend.
  const listing = useMatch("/listings/:listingId");
  if (listing?.params.listingId) {
    return { page: "listing_detail", listing_id: listing.params.listingId };
  }
  if (location.pathname === "/") {
    return { page: "listings_search" };
  }
  return { page: "other" };
}

function Suggestions({ ctx, onPick }: { ctx: ChatContext; onPick: (text: string) => void }) {
  const ideas =
    ctx.page === "listing_detail"
      ? [
          "Is this house priced well vs the comps you've seen?",
          "What are the biggest red flags here?",
          "How does this compare to other listings I'm tracking?",
        ]
      : [
          "Which active listings best match my criteria?",
          "Median list-vs-sold delta in Holmdel last 60 days?",
          "Any active listings priced below comps?",
        ];
  return (
    <div className="muted" style={{ fontSize: 12 }}>
      <p style={{ margin: "0 0 6px" }}>Try:</p>
      <ul style={{ paddingLeft: 16, margin: 0 }}>
        {ideas.map((t) => (
          <li key={t} style={{ marginBottom: 4 }}>
            <button
              onClick={() => onPick(t)}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--accent)",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
              }}
            >
              {t}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  const text = messageText(message);
  return (
    <div
      style={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        // Markdown replies (tables especially) need the extra width.
        maxWidth: isUser ? "85%" : "100%",
        background: isUser ? "var(--accent)" : "var(--panel)",
        color: isUser ? "white" : "var(--text)",
        padding: "8px 11px",
        borderRadius: 10,
        border: isUser ? "none" : "1px solid var(--border)",
        whiteSpace: isUser ? "pre-wrap" : "normal",
        lineHeight: 1.4,
        fontSize: 13,
        // Keeps a wide table from stretching the flex column past the panel.
        minWidth: 0,
      }}
    >
      {isUser ? linkifyPlain(text) : <ChatMarkdown text={text} compact />}
    </div>
  );
}

function applyUiAction(
  a: UiAction,
  store: {
    setFilters: (next: Partial<ListingsFilters>) => void;
    setHighlight: (ids: string[]) => void;
    clearHighlight: () => void;
  },
) {
  if (a.action === "highlight_listings") {
    const ids = (a.input?.listing_ids as unknown[] | undefined) ?? [];
    store.setHighlight(ids.filter((x): x is string => typeof x === "string"));
    return;
  }
  if (a.action === "clear_highlight") {
    store.clearHighlight();
    return;
  }
  if (a.action === "set_filters") {
    const allowed: (keyof ListingsFilters)[] = [
      "status",
      "minPrice",
      "maxPrice",
      "minSqft",
      "minBeds",
      "minBaths",
      "minSchoolScore",
    ];
    // Map snake_case keys from the model's tool input to the store's camelCase.
    const keyMap: Record<string, keyof ListingsFilters> = {
      status: "status",
      min_price: "minPrice",
      max_price: "maxPrice",
      min_sqft: "minSqft",
      min_beds: "minBeds",
      min_baths: "minBaths",
      min_school_score: "minSchoolScore",
    };
    const patch: Partial<ListingsFilters> = {};
    for (const [k, v] of Object.entries(a.input || {})) {
      const dest = keyMap[k];
      if (!dest || !allowed.includes(dest)) continue;
      // null intentionally clears the field; empty string treated as "no change".
      if (v === "" || v === undefined) continue;
      // status is a string enum, the others are number | null.
      if (dest === "status") {
        if (v === "active" || v === "pending" || v === "sold" || v === "all") {
          patch.status = v;
        }
        continue;
      }
      if (v === null) {
        (patch as Record<string, unknown>)[dest] = null;
      } else if (typeof v === "number" && Number.isFinite(v)) {
        (patch as Record<string, unknown>)[dest] = v;
      }
    }
    if (Object.keys(patch).length > 0) {
      store.setFilters(patch);
    }
  }
}
